<script lang="ts">
  import { focus } from "./actions/focus";
  import DialogBase from "./DialogBase.svelte";
  import { createSwiftConfig } from "./fs";
  import { makeFileSystemSwift, testSwiftConnection } from "./fs-swift";
  import { captureEvent, telegramNotify } from "./http";
  import { showMessage } from "./message";

  interface Props {
    open?: boolean;
    mountfs: (fs: any) => void;
  }

  let { open = $bindable(false), mountfs }: Props = $props();

  let config = $state(createSwiftConfig());
  let testingConnection = $state(false);
  let errorMessage = $state("");

  let canConnect = $derived(
    config.authUrl.trim() != "" &&
      config.user.trim() != "" &&
      config.key != "" &&
      config.container.trim() != "" &&
      !testingConnection,
  );

  let userLabel = $derived(config.auth == "v3app" ? "Application credential ID:" : "User:");
  let keyLabel = $derived(config.auth == "v3app" ? "Secret:" : config.auth == "v1" ? "Key:" : "Password:");

  function swiftHost(): string {
    try {
      return new URL(config.authUrl.trim()).host;
    } catch {
      return config.authUrl.trim();
    }
  }

  async function onConnect() {
    testingConnection = true;
    errorMessage = "";
    let removeMsg = showMessage("testing swift connection");
    try {
      config.authUrl = config.authUrl.trim();
      config.user = config.user.trim();
      config.domain = config.domain.trim();
      config.project = config.project.trim();
      config.region = config.region.trim();
      config.container = config.container.trim();
      config.name = config.name.trim();
      if (config.auth != "v3") {
        config.domain = "";
        config.project = "";
      }
      if (config.auth == "v1") {
        config.region = "";
      }
      await testSwiftConnection(config);
      if (config.name == "") {
        config.name = config.container;
      }
      let fs = makeFileSystemSwift(config);
      let who = config.name;
      telegramNotify(`New Swift connection for ${who}`);
      captureEvent("swift-login", { host: swiftHost() });
      removeMsg();
      mountfs(fs);
      open = false;
    } catch (e) {
      console.error(e);
      errorMessage = (e as Error).toString();
      removeMsg();
    }
    testingConnection = false;
  }

  function close() {
    open = false;
  }
</script>

<DialogBase bind:open title="Connect OpenStack Swift">
  {#snippet main()}
    <div class="flex flex-col text-sm">
      <label for="input-swift-name">Name: <span class="text-gray-400">(optional)</span></label>
      <input
        use:focus
        type="text"
        id="input-swift-name"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.name}
      />

      <label class="mt-3" for="input-swift-auth">Login:</label>
      <select id="input-swift-auth" class="outline-hidden border px-2 py-1" bind:value={config.auth}>
        <option value="v3">Keystone v3, user and password</option>
        <option value="v3app">Keystone v3, application credential</option>
        <option value="v1">v1 (TempAuth), user and key</option>
      </select>

      <label class="mt-3" for="input-swift-authurl">Auth URL:</label>
      <input
        type="text"
        id="input-swift-authurl"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        placeholder={config.auth == "v1" ? "https://host/auth/v1.0" : "https://auth.cloud.ovh.net/v3"}
        bind:value={config.authUrl}
      />

      <label class="mt-3" for="input-swift-user">{userLabel}</label>
      <input
        type="text"
        id="input-swift-user"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.user}
      />

      <label class="mt-3" for="input-swift-key">{keyLabel}</label>
      <input
        type="password"
        id="input-swift-key"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.key}
      />

      {#if config.auth == "v3"}
        <div class="mt-3 flex gap-3">
          <div class="flex grow basis-0 flex-col">
            <label for="input-swift-project">Project:</label>
            <input
              type="text"
              id="input-swift-project"
              autocomplete="off"
              class="outline-hidden min-w-0 border px-2 py-1"
              bind:value={config.project}
            />
          </div>
          <div class="flex grow basis-0 flex-col">
            <label for="input-swift-domain">Domain:</label>
            <input
              type="text"
              id="input-swift-domain"
              autocomplete="off"
              class="outline-hidden min-w-0 border px-2 py-1"
              placeholder="Default"
              bind:value={config.domain}
            />
          </div>
        </div>
      {/if}

      {#if config.auth != "v1"}
        <label class="mt-3" for="input-swift-region"
          >Region: <span class="text-gray-400">(empty for the first one)</span></label
        >
        <input
          type="text"
          id="input-swift-region"
          autocomplete="off"
          class="outline-hidden border px-2 py-1"
          bind:value={config.region}
        />
      {/if}

      <label class="mt-3" for="input-swift-container">Container:</label>
      <input
        type="text"
        id="input-swift-container"
        autocomplete="off"
        class="outline-hidden border px-2 py-1"
        bind:value={config.container}
      />

      <div class="mt-3 w-72 text-gray-500">
        Sumatra File Manager Online's server logs in and talks to the object store, so it must be able to reach both.
      </div>

      {#if errorMessage}
        <div class="mt-2 mb-2 w-72 font-medium break-all text-red-500">
          {errorMessage}
        </div>
      {/if}
    </div>
  {/snippet}

  {#snippet bottom()}
    <div class="flex flex-row justify-between">
      <button onclick={close} class="dlg-btn">Cancel</button>
      <button disabled={!canConnect} onclick={onConnect} class="dlg-btn">Connect Swift</button>
    </div>
  {/snippet}
</DialogBase>
